import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { RobotoTooltip } from "@/shared/components";
import {
  ThreadVisibility,
  type AgentThreadRecord,
} from "@/shared/domain/agentThread";

import styles from "./ThreadVisibilityBadge.module.css";

interface ThreadVisibilityBadgeProps {
  thread: AgentThreadRecord;
}

/**
 * Persistent privacy indicator for a thread's metadata header.
 *
 * Renders a lock pill only when the thread is ``PRIVATE`` — an ``ORG``
 * thread is visible to the whole org, so there is nothing to flag. A fork
 * (``forked_from_thread_id`` set) reads "Private fork" so the user who
 * landed here from a shared link understands they are looking at their own
 * private copy, not the original. Other private threads read "Private".
 *
 * This complements the one-time "Created a private fork" notification: the
 * toast is the in-the-moment cue, the pill is the durable one that survives
 * a page reload or a later visit.
 */
export function ThreadVisibilityBadge({ thread }: ThreadVisibilityBadgeProps) {
  if (thread.visibility !== ThreadVisibility.PRIVATE) {
    return null;
  }

  const label = thread.forked_from_thread_id ? "Private fork" : "Private";

  return (
    <RobotoTooltip title="This thread is private to you">
      <span className={styles.badge}>
        <LockOutlinedIcon className={styles.icon} aria-hidden="true" />
        {label}
      </span>
    </RobotoTooltip>
  );
}
